/**
 * HowItWorks - Interactive stepper with auto-rotating screenshots
 *
 * Features:
 * - Split layout: steps on left, screenshot on right
 * - Auto-advance every 5 seconds with progress indicator
 * - Click to select step (pauses autoplay temporarily)
 * - Smooth image transitions
 * - Mobile: vertical stack
 */

"use client";

import { useState, useEffect, useRef } from "react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { ScrollReveal, ShimmerText } from "./effects";

const AUTOPLAY_INTERVAL = 5000; // 5 seconds per step
const STEPS = ["song", "guitar", "preset"] as const;

export default function HowItWorks() {
  const t = useTranslations("landing.howItWorks");
  const tVisualProof = useTranslations("landing.visualProof");
  const tA11y = useTranslations("a11y");
  const [activeStep, setActiveStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Use refs to track state without causing re-renders
  const progressRef = useRef(0);
  const stepRef = useRef(0);

  // Handle step click
  const handleStepClick = (index: number) => {
    stepRef.current = index;
    progressRef.current = 0;
    setActiveStep(index);
    setProgress(0);
    setIsPaused(true);
    // Resume autoplay after 10 seconds of inactivity
    setTimeout(() => setIsPaused(false), 10000);
  };

  // Autoplay timer - sequential loop: 0 → 1 → 2 → 0
  useEffect(() => {
    if (isPaused) return;

    const tick = 50; // ms between updates
    const increment = 100 / (AUTOPLAY_INTERVAL / tick);

    const interval = setInterval(() => {
      progressRef.current += increment;

      if (progressRef.current >= 100) {
        // Move to next step
        stepRef.current = (stepRef.current + 1) % STEPS.length;
        progressRef.current = 0;
        setActiveStep(stepRef.current);
      }

      setProgress(progressRef.current);
    }, tick);

    return () => clearInterval(interval);
  }, [isPaused]);

  return (
    <section className="py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal>
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 md:mb-16">
            <ShimmerText>{t("title")}</ShimmerText>
          </h2>
        </ScrollReveal>

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 lg:items-center">
          {/* Left side: Steps list */}
          <div className="w-full lg:w-2/5 space-y-4">
            {STEPS.map((step, index) => {
              const isActive = activeStep === index;
              return (
                <button
                  key={step}
                  onClick={() => handleStepClick(index)}
                  className={`
                    w-full text-left p-5 rounded-xl transition-all duration-300
                    border relative overflow-hidden group
                    ${
                      isActive
                        ? "border-pink-500/50 shadow-[0_0_30px_-10px_rgba(236,72,153,0.4)]"
                        : "border-white/10 hover:border-white/20 hover:-translate-y-0.5 shadow-[0_0_20px_-10px_rgba(156,163,175,0.2)]"
                    }
                  `}
                >
                  {/* Base gradient background */}
                  <div
                    className={`
                      absolute inset-0 transition-all duration-300
                      ${
                        isActive
                          ? "bg-gradient-to-br from-pink-500/10 via-purple-500/5 to-transparent"
                          : "bg-gradient-to-br from-white/5 via-white/2 to-transparent group-hover:from-white/8"
                      }
                    `}
                  />

                  {/* Top highlight line */}
                  <div
                    className={`
                      absolute top-0 left-4 right-4 h-[1px]
                      transition-opacity duration-300
                      ${
                        isActive
                          ? "bg-gradient-to-r from-transparent via-pink-400/25 to-transparent"
                          : "bg-gradient-to-r from-transparent via-gray-400/10 to-transparent"
                      }
                    `}
                  />

                  {/* Progress bar for active step */}
                  {isActive && (
                    <div
                      className="absolute bottom-0 left-0 h-[2px] bg-gradient-to-r from-pink-500 to-purple-500 transition-all duration-50"
                      style={{ width: `${progress}%` }}
                    />
                  )}

                  <div className="flex items-start gap-4">
                    {/* Step number */}
                    <div
                      className={`
                        w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0
                        transition-all duration-300
                        ${
                          isActive
                            ? "bg-gradient-to-br from-pink-600 to-purple-600"
                            : "bg-gray-700/50 group-hover:bg-gray-600/50"
                        }
                      `}
                    >
                      <span
                        className={`font-bold ${isActive ? "text-white" : "text-gray-400"}`}
                      >
                        {index + 1}
                      </span>
                    </div>

                    {/* Step content */}
                    <div className="flex-1 min-w-0">
                      <h3
                        className={`
                          font-semibold text-lg mb-1 transition-colors duration-300
                          ${isActive ? "text-white" : "text-gray-300"}
                        `}
                      >
                        {t(`steps.${step}.title`)}
                      </h3>
                      <p
                        className={`
                          text-sm transition-colors duration-300
                          ${isActive ? "text-gray-300" : "text-gray-500"}
                        `}
                      >
                        {t(`steps.${step}.description`)}
                      </p>
                      {step === "preset" && (
                        <p
                          className={`
                            text-xs italic mt-2 transition-colors duration-300
                            ${isActive ? "text-gray-400" : "text-gray-600"}
                          `}
                        >
                          {t("steps.preset.outcome")}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}

            {/* Step indicators */}
            <div className="flex justify-center gap-2 pt-4">
              {STEPS.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleStepClick(index)}
                  className={`
                    w-2 h-2 rounded-full transition-all duration-300
                    ${
                      activeStep === index
                        ? "w-6 bg-pink-500"
                        : "bg-gray-600 hover:bg-gray-500"
                    }
                  `}
                  aria-label={tA11y("goToStep", { step: index + 1 })}
                />
              ))}
            </div>
          </div>

          {/* Right side: Screenshot */}
          <div className="w-full lg:w-3/5">
            <ScrollReveal className="w-full">
              {/* Image container with hover glow */}
              <div className="relative w-full aspect-[16/9] rounded-xl overflow-hidden shadow-2xl">
                {STEPS.map((step, index) => (
                  <Image
                    key={step}
                    src={`/images/landing/step-${index + 1}.png`}
                    alt={t(`steps.${step}.title`)}
                    fill
                    sizes="(max-width: 1024px) 100vw, 60vw"
                    className={`
                      transition-opacity duration-500 object-contain object-center
                      ${activeStep === index ? "opacity-100" : "opacity-0"}
                    `}
                    priority={index === 0}
                  />
                ))}
              </div>
            </ScrollReveal>
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-center text-gray-500 text-sm mt-8 max-w-2xl mx-auto">
          {tVisualProof("disclaimer")}
        </p>
      </div>
    </section>
  );
}
